import { Roi } from './roi.model';

/**
 * Type representing ROI finding metadata
 * Used to track ROI-related findings separately from text/classification findings
 * Extends Roi but omits unnecessary fields for finding tracking
 */
export type RoiFinding = Omit<Roi, 'id' | 'uuid' | 'coordinates' | 'labels' | 'url' | 'additionalLabelsIncluded'>;

/**
 * Generates a unique key for ROI finding lookups
 * Format: "creatorId:assetId:pipelineStepId"
 */
export function generateRoiFindingKey(creatorId: string, assetId: string, pipelineStepId: string): string {
  return `${creatorId}:${assetId}:${pipelineStepId}`;
}

export class RoiFindingMapper {
  public static fromRois(rois: Roi[]): Map<string, RoiFinding> {
    const result = new Map<string, RoiFinding>();

    for (const roi of rois) {
      const key = generateRoiFindingKey(roi.creatorId, roi.assetId, roi.pipelineStepId);

      if (!result.has(key)) {
        result.set(key, this.fromRoi(roi));
      }
    }

    return result;
  }

  public static fromList(findings: RoiFinding[]): Map<string, RoiFinding> {
    const result = new Map<string, RoiFinding>();

    for (const finding of findings) {
      const key = generateRoiFindingKey(finding.creatorId, finding.assetId, finding.pipelineStepId);
      result.set(key, finding);
    }

    return result;
  }


  public static merge(
    secondary: Map<string, RoiFinding>,
    primary: Map<string, RoiFinding>
  ): Map<string, RoiFinding> {
    return new Map([...secondary, ...primary]);
  }

  private static fromRoi(roi: Roi): RoiFinding {
    return {
      assetId: roi.assetId,
      sampleId: roi.sampleId,
      analysisId: roi.analysisId,
      pipelineId: roi.pipelineId,
      findingType: roi.findingType,
      creatorId: roi.creatorId,
      creatorEntity: roi.creatorEntity,
      findingVersionId: roi.findingVersionId,
      pipelineStepId: roi.pipelineStepId,
      findingVersion: roi.findingVersion,
      findingId: roi.findingId,
      originFindingVersionId: roi.originFindingVersionId,
    };
  }
}
